import React, {useEffect, useRef, useState} from 'react';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import {Helmet} from 'react-helmet';
import './index.scss';
import Input from '../../components/Input';
import Select from 'react-select';
import ToolsRow from '../../components/ToolsComponents/ToolsRow';
import CustomPaginate from '../../components/PaginateComponent';
import {
  useCreateEquipmentMutation,
  useEquipmentActivateMutation,
  useLazyGetCalibrationsQuery,
  useLazyGetEquipmentQuery,
  useLazyGetFacilityQuery,
  useLazyGetReportHistoryQuery,
  useLazyGetReportsQuery,
  useLazyGetToolsQuery,
} from '../../store/builder/inspections.api';
import {useDebounce} from '../LogSheets/hooks';
import {useDispatch} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {setLoader} from '../../store/builder/reducer';
import Datepicker from '../../components/Datepicker';
import moment from 'moment/moment';
import ReportHistory from '../../components/ToolsComponents/CalibrationHistory';
import {toast} from 'react-toastify';
import {useTranslation} from '../../hooks/useTranslation';
import DecommissionToolModal from './components/DecommissionToolModal/DecommissionToolModal';
import DeleteToolModal from './components/DeleteToolModal/DeleteToolModal';
import ArchiveToolModal from './components/ArchiveToolModal/ArchiveToolModal';
import ActivateToolModal from './components/ActivateToolModal/ActivateToolModal';

const ToolsManagement = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const currentFbo = localStorage.getItem('current-fbo');
  const [currentTab, setCurrentTab] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [reportModal, setReportModal] = useState(false);
  const [activeReport, setActiveReport] = useState(false);
  const [reportTitle, setReportTitle] = useState('');
  const [currentTool, setCurrentTool] = useState(null);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([
    {label: t('toolsManagement.typeOptions.all'), value: 'all'},
    {label: t('toolsManagement.typeOptions.new'), value: 'new'},
    {label: t('toolsManagement.typeOptions.completed'), value: 'completed'},
  ]);
  const [confirmModal, setConfirmModal] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [archiveModal, setArchiveModal] = useState(false);
  const [activateModal, setActivateModal] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef();
  const [toolsFilters, setToolsFilters] = useState({
    facility_id: null,
  });
  const [calibrationFilters, setCalibrationFilters] = useState({
    date_from: null,
    date_to: null,
    facility_id: null,
    equipment_id: null,
    equipment_type: null,
  });
  const [verificationFilters, setVerificationFilters] = useState({
    date_from: null,
    date_to: null,
    facility_id: null,
    equipment_id: null,
    equipment_type: null,
  });
  const debouncedSearchValue = useDebounce(search, 500);

  const [
    getTools,
    {
      data: toolsData,
      error: toolsError,
      isError: toolsIsError,
      isSuccess: toolsIsSuccess,
      isFetching: toolsIsLoading,
    },
  ] = useLazyGetToolsQuery();
  const [
    getCalibrations,
    {
      data: calibrationsData,
      error: calibrationsError,
      isError: calibrationsIsError,
      isSuccess: calibrationsIsSuccess,
      isFetching: calibrationsIsLoading,
    },
  ] = useLazyGetCalibrationsQuery();

  const [
    getFacility,
    {
      data: facilityData,
      isError: facilityIsError,
      error: facilityError,
      isSuccess: facilityIsSuccess,
      isFetching: facilityIsLoading,
    },
  ] = useLazyGetFacilityQuery();

  const [
    getEquipment,
    {
      data: equipmentData,
      isError: equipmentIsError,
      isSuccess: equipmentIsSuccess,
      isFetching: equipmentIsLoading,
      error: equipmentError,
    },
  ] = useLazyGetEquipmentQuery();

  const [
    activate,
    {data: activateData, isLoading: activateIsLoading, error: activateError},
  ] = useEquipmentActivateMutation();

  const [
    getReports,
    {data: reportsData, isLoading: reportsLoading, error: reportsError},
  ] = useLazyGetReportsQuery();
  const [
    getReportHistory,
    {
      data: reportHistoryData,
      isLoading: reportHistoryLoading,
      error: reportHistoryError,
    },
  ] = useLazyGetReportHistoryQuery();

  const [
    createEquipment,
    {
      isFetching: createEquipmentIsLoading,
      error: createEquipmentError,
      isError: createEquipmentIsError,
      isSuccess: createEquipmentIsSuccess,
    },
  ] = useCreateEquipmentMutation();

  useEffect(() => {
    getFacility();
    getEquipment({active: true, equipment_type_id: '44'});
  }, []);

  useEffect(() => {
    if (currentTab === 1) {
      getTools({
        page: currentPage,
        search: debouncedSearchValue,
        facility_id: toolsFilters?.facility_id,
        active: true,
      });
    }
    if (currentTab === 4) {
      getTools({
        page: currentPage,
        search: debouncedSearchValue,
        facility_id: toolsFilters?.facility_id,
        active: false,
      });
    }
    if (currentTab === 2) {
      getCalibrations({
        agency_id: currentFbo,
        inspection_type: 'calibration',
        search: debouncedSearchValue,
        date_from: calibrationFilters?.date_from,
        date_to: calibrationFilters?.date_to,
        equipment_id: calibrationFilters?.equipment_id,
        is_complete: calibrationFilters?.equipment_type,
      });
    }
    if (currentTab === 3) {
      getCalibrations({
        agency_id: currentFbo,
        inspection_type: 'verification',
        search: debouncedSearchValue,
        date_from: verificationFilters?.date_from,
        date_to: verificationFilters?.date_to,
        equipment_id: verificationFilters?.equipment_id,
        is_complete: verificationFilters?.equipment_type,
      });
    }
    if (currentTab === 5) {
      getReports({agency_id: currentFbo});
    }
    if (currentTab === 6) {
      getTools({
        page: currentPage,
        search: debouncedSearchValue,
        facility_id: toolsFilters?.facility_id,
        archived: true,
      });
    }
  }, [
    currentPage,
    currentTab,
    debouncedSearchValue,
    toolsFilters,
    calibrationFilters,
    verificationFilters,
  ]);

  useEffect(() => {
    if (
      toolsIsLoading ||
      calibrationsIsLoading ||
      activateIsLoading ||
      reportsLoading ||
      reportHistoryLoading
    ) {
      dispatch(setLoader(true));
    } else {
      dispatch(setLoader(false));
    }
  }, [
    toolsIsLoading,
    calibrationsIsLoading,
    activateIsLoading,
    reportsLoading,
    reportHistoryLoading,
  ]);

  useEffect(() => {
    if (facilityIsSuccess) {
      setFacilityOptions(
        facilityData?.map(item => ({
          value: item.id,
          label: item.name,
        })),
      );
    }
    if (equipmentIsSuccess) {
      setEquipmentOptions(
        equipmentData?.data?.map(item => ({
          value: item.id,
          label: item.title,
        })),
      );
    }
  }, [facilityIsSuccess, equipmentIsSuccess]);

  const handleSearch = e => {
    setSearch(e.target.value);
  };

  const changeTab = tabId => {
    setCurrentTab(tabId);
    setSearch('');
    searchRef.current.value = '';
    setCurrentPage(1);
    setCalibrationFilters({
      date_from: null,
      date_to: null,
      facility_id: null,
      equipment_id: null,
    });
    setToolsFilters({facility_id: null});
  };

  const handlePageClick = page => {
    setCurrentPage(page.selected + 1);
    window.scrollTo(0, 0);
  };

  const getToolsActive = () => {
    getTools({
      page: currentPage,
      search: debouncedSearchValue,
      facility_id: toolsFilters?.facility_id,
      active: true,
    });
  };

  const openReportModal = async (title, item) => {
    setReportTitle(title);
    setReportModal(true);
    dispatch(setLoader(true));
    await getReportHistory({equipment_id: item?.equipment_id})
      .unwrap()
      .then(resp => {
        setActiveReport(resp?.[0]);
      })
      .catch(err => {
        toast.error(err.response.data.message);
      })
      .finally(() => {
        dispatch(setLoader(false));
      });
  };

  const getToolsDecommissioned = () => {
    getTools({
      page: currentPage,
      search: debouncedSearchValue,
      facility_id: toolsFilters?.facility_id,
      active: false,
    });
  };

  return (
    <Wrapper>
      <DashboardHeader
        type={'tools'}
        title={t('toolsManagement.title')}
        action={() => navigate('/tools/create')}
      />
      {confirmModal && (
        <DecommissionToolModal
          toolId={currentId}
          setIsOpen={setConfirmModal}
          onDecommissionSuccess={getToolsActive}
        />
      )}
      {deleteModal && (
        <DeleteToolModal
          toolId={currentId}
          setIsOpen={setDeleteModal}
          onDeleteSuccess={getToolsDecommissioned}
        />
      )}
      {archiveModal && (
        <ArchiveToolModal
          toolId={currentId}
          setIsOpen={setArchiveModal}
          onArchiveSuccess={getToolsDecommissioned}
        />
      )}
      {activateModal && (
        <ActivateToolModal
          toolId={currentId}
          setIsOpen={setActivateModal}
          isArchived={currentTab === 6}
          onActivateSuccess={() => {
            if (currentTab === 1) {
              getTools({
                page: currentPage,
                search: debouncedSearchValue,
                facility_id: toolsFilters?.facility_id,
                active: true,
              });
            } else if (currentTab === 4) {
              getTools({
                page: currentPage,
                search: debouncedSearchValue,
                facility_id: toolsFilters?.facility_id,
                active: false,
              });
            } else if (currentTab === 6) {
              getTools({
                page: currentPage,
                search: debouncedSearchValue,
                facility_id: toolsFilters?.facility_id,
                archived: true,
              });
            }
          }}
        />
      )}
      {reportModal && (
        <ReportHistory
          exit={() => setReportModal(false)}
          data={activeReport}
          title={reportTitle}
        />
      )}
      <Helmet>
        <title>{t('toolsManagement.pageTitle')}</title>
      </Helmet>
      <div className='tools'>
        <div className='tools__header'>
          <div
            className={`tools__header-tab ${currentTab == 5 ? 'active' : ''}`}
            onClick={() => changeTab(5)}
          >
{t('toolsManagement.tabs.reports')}
          </div>
          <div
            className={`tools__header-tab ${currentTab == 1 ? 'active' : ''}`}
            onClick={() => changeTab(1)}
          >
            {t('toolsManagement.tabs.tools')}
          </div>
          <div
            className={`tools__header-tab ${currentTab == 4 ? 'active' : ''}`}
            onClick={() => changeTab(4)}
          >
{t('toolsManagement.tabs.toolsDecommissioned')}
          </div>
          <div
            className={`tools__header-tab ${currentTab == 6 ? 'active' : ''}`}
            onClick={() => changeTab(6)}
          >
            {t('equipmentTabs.archived')}
          </div>
          <div
            className={`tools__header-tab ${currentTab == 2 ? 'active' : ''}`}
            onClick={() => changeTab(2)}
          >
{t('toolsManagement.tabs.calibration')}
          </div>
          <div
            className={`tools__header-tab ${currentTab == 3 ? 'active' : ''}`}
            onClick={() => changeTab(3)}
          >
{t('toolsManagement.tabs.verification')}
          </div>
        </div>
        <div className={`tools__filters ${currentTab === 5 ? 'reports' : ''}`}>
          {currentTab == 1 || currentTab == 4 || currentTab == 6 ? (
            <>
              <Input
                innerRef={searchRef}
                value={search}
                placeholder={t('toolsManagement.searchPlaceholders.searchById')}
                image={'assets/icons/search.svg'}
                maxwidth={200}
                onChange={handleSearch}
              />
            </>
          ) : currentTab == 2 ? (
            <>
              <Input
                innerRef={searchRef}
                value={search}
                placeholder={t('toolsManagement.searchPlaceholders.searchByDescription')}
                image={'assets/icons/search.svg'}
                maxwidth={200}
                onChange={handleSearch}
              />
              <Datepicker
                type={'from'}
                setValue={e =>
                  setCalibrationFilters(old => ({
                    ...old,
                    date_from: moment(e)?.format('YYYY-MM-DD'),
                  }))
                }
                selected={calibrationFilters?.date_from}
              />
              <Datepicker
                type={'to'}
                setValue={e =>
                  setCalibrationFilters(old => ({
                    ...old,
                    date_to: moment(e)?.format('YYYY-MM-DD'),
                  }))
                }
                selected={calibrationFilters?.date_to}
              />
              <Select
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                    borderRadius: 8,
                    width: 200,
                    height: 42,
                    backgroundColor: '#F4F5F6',
                  }),
                }}
                isClearable
                key={JSON.stringify(equipmentOptions) + '1'}
                placeholder={t('toolsManagement.filters.equipment')}
                options={equipmentOptions}
                defaultValue={
                  equipmentOptions?.filter(
                    item => item?.value == calibrationFilters?.equipment_id,
                  )[0]
                }
                onChange={e => {
                  setCalibrationFilters(old => ({
                    ...old,
                    equipment_id: e?.value,
                  }));
                }}
              />
              <Select
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                    borderRadius: 8,
                    width: 200,
                    height: 42,
                    backgroundColor: '#F4F5F6',
                  }),
                }}
                key={JSON.stringify(typeOptions) + '1'}
                placeholder={t('toolsManagement.filters.type')}
                options={typeOptions}
                defaultValue={typeOptions[0]}
                onChange={e => {
                  setCalibrationFilters(old => ({
                    ...old,
                    equipment_type: e?.value,
                  }));
                }}
              />
            </>
          ) : currentTab == 3 ? (
            <>
              <Input
                innerRef={searchRef}
                value={search}
                placeholder={t('toolsManagement.searchPlaceholders.searchByDescription')}
                image={'assets/icons/search.svg'}
                maxwidth={200}
                onChange={handleSearch}
              />
              <Datepicker
                type={'from'}
                setValue={e =>
                  setVerificationFilters(old => ({
                    ...old,
                    date_from: moment(e)?.format('YYYY-MM-DD'),
                  }))
                }
                selected={verificationFilters?.date_from}
              />
              <Datepicker
                type={'to'}
                setValue={e =>
                  setVerificationFilters(old => ({
                    ...old,
                    date_to: moment(e)?.format('YYYY-MM-DD'),
                  }))
                }
                selected={verificationFilters?.date_to}
              />
              <Select
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                    borderRadius: 8,
                    width: 200,
                    height: 42,
                    backgroundColor: '#F4F5F6',
                  }),
                }}
                isClearable
                key={JSON.stringify(equipmentOptions) + '1'}
                placeholder={t('toolsManagement.filters.equipment')}
                options={equipmentOptions}
                defaultValue={
                  equipmentOptions?.filter(
                    item => item?.value == verificationFilters?.equipment_id,
                  )[0]
                }
                onChange={e => {
                  setVerificationFilters(old => ({
                    ...old,
                    equipment_id: e?.value,
                  }));
                }}
              />
              <Select
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                    borderRadius: 8,
                    width: 200,
                    height: 42,
                    backgroundColor: '#F4F5F6',
                  }),
                }}
                key={JSON.stringify(typeOptions) + '1'}
                placeholder={t('toolsManagement.filters.type')}
                options={typeOptions}
                defaultValue={typeOptions[0]}
                onChange={e => {
                  setVerificationFilters(old => ({
                    ...old,
                    equipment_type: e?.value,
                  }));
                }}
              />
            </>
          ) : null}
        </div>
        <div className='tools__box'>
          <div className='tools__table'>
            <div
              className={`tools__table-head ${currentTab == 1 ? 'tool' : currentTab == 2 ? 'calibration' : currentTab == 3 ? 'verification' : currentTab == 4 ? 'decommission' : currentTab == 6 ? 'archived' : ''}`}
            >
              {currentTab == 1 ? (
                <>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.id')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.toolDescription')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.calibrationPeriod')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.lastCalibrationDate')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.action')}</div>
                </>
              ) : currentTab == 4 ? (
                <>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.id')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.toolDescription')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.calibrationPeriod')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.lastCalibrationDate')}</div>
                  <div className='tools__table-th w-340'>{t('toolsManagement.tableHeaders.action')}</div>
                </>
              ) : currentTab === 2 ? (
                <>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.id')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.toolDescription')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.dateOfCalibration')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.calibratedBy')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.calibrationDueDate')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.comment')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.status')}</div>
                </>
              ) : currentTab === 3 ? (
                <>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.id')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.toolDescription')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.dateOfVerification')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.verifiedBy')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.verificationDueDate')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.comment')}</div>
                  <div className='tools__table-th'>{t('toolsManagement.tableHeaders.status')}</div>
                </>
              ) : currentTab === 6 ? (
                        <>
                          <div className='tools__table-th'>{t('equipmentTable.headers.type')}</div>
                          <div className='tools__table-th'>{t('equipmentTable.headers.id')}</div>
                          <div className='tools__table-th'>{t('equipmentTable.headers.fuelType')}</div>
                          <div className='tools__table-th'>{t('equipmentTable.headers.facility')}</div>
                          <div className='tools__table-th'>{t('equipmentTable.headers.nfc')}</div>
                          <div className='tools__table-th w-340'>{t('toolsManagement.tableHeaders.action')}</div>
                </>
              ) : null}
            </div>
            <div
              className={`tools__table-body ${currentTab == 1 ? 'full' : ''}`}
            >
              {currentTab === 1 || currentTab === 4 || currentTab === 6 ? (
                toolsData?.data?.length > 0 ? (
                  toolsData?.data?.map((tool, index) => (
                    <ToolsRow
                      currentId={currentId}
                      setCurrentId={setCurrentId}
                      setConfirmModal={setConfirmModal}
                      activate={activate}
                      tool={tool}
                      currentTool={currentTool}
                      setCurrentTool={setCurrentTool}
                      currentTab={currentTab}
                      key={index}
                      setDeleteModal={setDeleteModal}
                      setArchiveModal={setArchiveModal}
                      setActivateModal={setActivateModal}
                      getToolsDecom={() =>
                        getTools({
                          page: currentPage,
                          search: debouncedSearchValue,
                          facility_id: toolsFilters?.facility_id,
                          active: false,
                        })
                      }
                    />
                  ))
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '205px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      paddingTop: 100,
                      paddingBottom: 100,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                      }}
                    >
                      <div className='expired__box-img' style={{margin: '0'}}>
                        <img src='/assets/no-data.svg' alt='no-data' />
                      </div>
                      <p className='no-payment-text'>{t('toolsManagement.messages.noData')}</p>
                    </div>
                  </div>
                )
              ) : currentTab === 2 || currentTab === 3 ? (
                calibrationsData?.data?.length > 0 ? (
                  calibrationsData?.data?.map((tool, index) => (
                    <ToolsRow
                      tool={tool}
                      currentTool={currentTool}
                      setCurrentTool={setCurrentTool}
                      currentTab={currentTab}
                      key={index}
                    />
                  ))
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '205px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      paddingTop: 100,
                      paddingBottom: 100,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                      }}
                    >
                      <div className='expired__box-img' style={{margin: '0'}}>
                        <img src='/assets/no-data.svg' alt='no-data' />
                      </div>
                      <p className='no-payment-text'>{t('toolsManagement.messages.noData')}</p>
                    </div>
                  </div>
                )
              ) : null}
            </div>
          </div>
          {currentTab === 5 ? (
            <div className='tools__reportsContent'>
              <div>
                <span className='tools__tableTitle'>{t('toolsManagement.reports.calibration')}</span>
                <div className='tools__table'>
                  <div className={`tools__table-head reports`}>
                    <div className='tools__table-th'>{t('toolsManagement.tableHeaders.id')}</div>
                    <div className='tools__table-th'>{t('toolsManagement.tableHeaders.toolDescription')}</div>
                    <div className='tools__table-th'>{t('toolsManagement.tableHeaders.dateOfCalibration')}</div>
                    <div className='tools__table-th'>{t('toolsManagement.tableHeaders.calibratedBy')}</div>
                    <div className='tools__table-th'>{t('toolsManagement.tableHeaders.nextCalibrationDueDate')}</div>
                  </div>
                  <div className='tools__table-body reports'>
                    {reportsData?.calibration?.map((item, index) => (
                      <div
                        key={index}
                        className='tools__table-row reports '
                        onClick={() => openReportModal(t('toolsManagement.reports.calibration'), item)}
                      >
                        {/*<div key={index} className="tools__table-row reports ">*/}
                        <div className='tools__table-bodyTh longText'>
                          <p className='tools__table-subtitle'>{item?.title}</p>
                        </div>
                        <div className='tools__table-bodyTh'>
                          <p className='tools__table-subtitle'>
                            {item?.description}
                          </p>
                        </div>
                        <div className='tools__table-bodyTh'>
                          <p className='tools__table-subtitle'>
                            {item?.calibration_date}
                          </p>
                        </div>
                        <div className='tools__table-bodyTh'>
                          <p className='tools__table-subtitle'>
                            {item?.calibrated_by}
                          </p>
                        </div>
                        <div className='tools__table-bodyTh'>
                          <p className='tools__table-subtitle'>
                            {item?.calibration_due_date}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <span className='tools__tableTitle'>{t('toolsManagement.reports.verification')}</span>
                <div className='tools__table'>
                  <div className={`tools__table-head reports`}>
                    <div className='tools__table-th'>{t('toolsManagement.tableHeaders.id')}</div>
                    <div className='tools__table-th'>{t('toolsManagement.tableHeaders.unitDescription')}</div>
                    <div className='tools__table-th'>{t('toolsManagement.tableHeaders.dateOfVerification')}</div>
                    <div className='tools__table-th'>{t('toolsManagement.tableHeaders.verifiedBy')}</div>
                    <div className='tools__table-th'>{t('toolsManagement.tableHeaders.nextVerificationDueDate')}</div>
                  </div>
                  <div className='tools__table-body'>
                    {reportsData?.verification?.map((item, index) => (
                      <div
                        key={index}
                        className='tools__table-row reports'
                        onClick={() => openReportModal(t('toolsManagement.reports.verification'), item)}
                      >
                        {/*<div key={index} className="tools__table-row reports">*/}
                        <div className='tools__table-bodyTh longText'>
                          <p className='tools__table-subtitle'>{item?.title}</p>
                        </div>
                        <div className='tools__table-bodyTh'>
                          <p className='tools__table-subtitle'>
                            {item?.description}
                          </p>
                        </div>
                        <div className='tools__table-bodyTh'>
                          <p className='tools__table-subtitle'>
                            {item?.calibration_date}
                          </p>
                        </div>
                        <div className='tools__table-bodyTh'>
                          <p className='tools__table-subtitle'>
                            {item?.calibrated_by}
                          </p>
                        </div>
                        <div className='tools__table-bodyTh'>
                          <p className='tools__table-subtitle'>
                            {item?.calibration_due_date}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          {currentTab === 1 || currentTab === 4 || currentTab === 6 ? (
            <CustomPaginate
              pageCount={toolsData?.pages}
              handlePageClick={handlePageClick}
            />
          ) : currentTab === 2 || currentTab === 3 ? (
            <CustomPaginate
              pageCount={calibrationsData?.pages}
              handlePageClick={handlePageClick}
            />
          ) : null}
        </div>
      </div>
    </Wrapper>
  );
};

export default ToolsManagement;
